import React, { useEffect, useState } from 'react'
import styled from 'styled-components'

import { theme } from '../../../theme'

type MessageProps = {
  children: React.ReactNode
  type: 'success' | 'error' | 'info' | 'warning'
  autoHide?: boolean
  autoHideDelay?: number
  onClose?: () => void
  className?: string
}

const MessageContainer = styled.div<{ type: MessageProps['type'] }>`
  padding: 1rem;
  border-radius: 0.375rem;
  border-width: 1px;
  font-size: 0.875rem;
  display: flex;
  align-items: flex-start;

  ${({ type }) => {
    switch (type) {
      case 'error':
        return `
          background-color: ${theme.colors.errorBg};
          color: ${theme.colors.errorText}; 
          border-color: ${theme.colors.errorBorder};
        `
      case 'info':
        return `
          background-color: ${theme.colors.infoBg};
          color: ${theme.colors.infoText};
          border-color: ${theme.colors.infoBorder};
        `
      case 'warning':
        return `
          background-color: ${theme.colors.warningBg};
          color: ${theme.colors.warningText};
          border-color: ${theme.colors.warningBorder};
        `
      case 'success':
      default:
        return `
              background-color: ${theme.colors.successBg};
              color: ${theme.colors.successText};
              border-color: ${theme.colors.successBorder};
            `
    }
  }}
`

const Icon = styled.i`
  margin-right: 0.5rem;
  margin-top: 0.125rem;
  flex-shrink: 0;
`

const Content = styled.div`
  flex: 1;
`

const CloseButton = styled.button`
  margin-left: 0.5rem;
  flex-shrink: 0;
  color: currentColor;

  &:hover {
    opacity: 0.7;
  }

  &:focus {
    outline: none;
  }
`

export const Message = ({
  children,
  type,
  autoHide = true,
  autoHideDelay = 10000,
  onClose,
  className = '',
}: MessageProps) => {
  const [isVisible, setIsVisible] = useState(true)

  useEffect(() => {
    if (autoHide && autoHideDelay > 0) {
      const timer = setTimeout(() => {
        setIsVisible(false)
        onClose?.()
      }, autoHideDelay)

      return () => clearTimeout(timer)
    }
  }, [autoHide, autoHideDelay, onClose])

  if (!isVisible) return null

  const icons = {
    success: 'fas fa-check-circle',
    error: 'fas fa-exclamation-circle',
    info: 'fas fa-info-circle',
    warning: 'fas fa-exclamation-triangle',
  }

  return (
    <MessageContainer type={type} className={className}>
      <Icon className={icons[type]} />
      <Content>{children}</Content>
      {onClose && (
        <CloseButton
          onClick={() => {
            setIsVisible(false)
            onClose()
          }}
        >
          <i className="fas fa-times"></i>
        </CloseButton>
      )}
    </MessageContainer>
  )
}
